import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiErrorMessage, ApiError } from "../../api/client";
import { VerdictBar } from "../../components/charts/BarChart";
import { scheduleLabel, handRoleLabel, type Trigger } from "../../lib/schedule";
import { formatWhen, todayInWorkspace } from "../../lib/when";
import { AgentSettings, type AgentScheduleView } from "../../components/AgentSettings";
import { usePersona } from "../../hooks/usePersona";

/**
 * The workforce roster (NEW-WF-2).
 *
 * One row per agent, with how its recent runs went. The verdict counts are
 * kept apart on purpose: an escalation is not a failure — it is the agent
 * doing the thing it was built to do — and a screen that added them together
 * would report a working system as a broken one.
 */

interface HandSummary {
  key: string;
  name: string;
  role_key: string | null;
  task_key: string | null;
  tier: number | null;
  schedule: string | null;
  trigger: Trigger | null;
  registered: boolean;
  /** The record an event-triggered Hand acts on, from its definition. */
  record: { key: string; label: string; example: string } | null;
  runs: number;
  auto_completed: number;
  escalated: number;
  blocked_pre: number;
  failed: number;
  last_run_at: string | null;
  next_run_at: string | null;
  scheduled: boolean;
  last_error: string | null;
  packs?: { pack_key: string; label: string; instrument: string }[];
  settings?: AgentScheduleView & {
    owner_name: string | null;
    owner_title: string | null;
  };
  decisions?: { key: string; label: string; approver_role: string; stage: string }[];
}

interface RunSummary {
  run_id: string;
  hand_key: string;
  hand_name: string;
  verdict: string;
  pre_verdict: string | null;
  missing: string[];
  anomaly_count: number;
  duration_ms: number;
  created_at: string;
  llm_model: string | null;
}

export function verdictLabel(verdict: string): string {
  switch (verdict) {
    case "auto_completed":
      return "Completed unattended";
    // Not "blocked" and not "failed": the deterministic checks stopped this
    // before the model was ever called. That is the system working.
    case "blocked_pre":
      return "Stopped before drafting";
    case "escalated":
      return "Sent to a person";
    case "failed":
      return "Failed";
    default:
      return verdict;
  }
}

export function verdictTone(verdict: string): string {
  switch (verdict) {
    case "auto_completed":
      return "green";
    case "escalated":
      return "amber";
    case "blocked_pre":
      return "blue";
    default:
      return "red";
  }
}

function relative(iso: string | null): string {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.round(hours / 24)} d ago`;
}

/** Where a run that threw stopped, in words a person can act on. */
function failedAtWords(stage: unknown): string {
  switch (stage) {
    case "load_inputs":
      return "reading its inputs";
    case "resolve_rules":
      return "fetching the rules";
    case "calculate":
      return "calculating";
    case "pre_gate":
      return "asking GraphIQ";
    case "model":
      return "calling the model";
    case "post_gate":
      return "checking the draft";
    default:
      return "before it could decide";
  }
}

/** The 422 a Run now gets when the agent threw: the run exists, open it. */
function recordedFailure(e: unknown): { runId: string; failedAt: unknown; detail: string } | null {
  if (!(e instanceof ApiError) || e.status !== 422) return null;
  const b = e.body as { error?: unknown; run_id?: unknown; failed_at?: unknown; detail?: unknown } | null;
  if (!b || b.error !== "run_failed" || typeof b.run_id !== "string") return null;
  return { runId: b.run_id, failedAt: b.failed_at, detail: typeof b.detail === "string" ? b.detail : "" };
}

function RunNow({ hand, workspaceId }: { hand: HandSummary; workspaceId: string }) {
  const qc = useQueryClient();
  // What this Hand acts on comes from its own definition. A table of Hands
  // kept here would drift the moment one was added.
  const spec = hand.record;
  // Today in the workspace's zone. A date hard-coded to the day the demo was
  // built means every run a client starts is dated to that day.
  const [asOf, setAsOf] = useState(todayInWorkspace);
  const [record, setRecord] = useState(spec?.example ?? "");

  const run = useMutation({
    mutationFn: () =>
      apiPost<{ run_id: string; verdict: string }>(
        `/api/workspaces/${workspaceId}/hand-runs`,
        {
          hand_key: hand.key,
          as_of: asOf,
          ...(spec && record ? { inputs: { [spec.key]: record } } : {}),
        },
      ),
    // On settle, not success: a run that could not complete is a row too
    // (D1), and the roster should show it without a refresh.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["workforce", workspaceId] });
      void qc.invalidateQueries({ queryKey: ["hand-runs", workspaceId] });
      void qc.invalidateQueries({ queryKey: ["forks", workspaceId] });
    },
  });
  const failure = run.isError ? recordedFailure(run.error) : null;

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 10, flexWrap: "wrap" }}
    >
      <label style={{ color: "var(--text-3)" }}>
        As at{" "}
        <input
          aria-label="As of date"
          type="date"
          value={asOf}
          onChange={(e) => setAsOf(e.target.value)} style={{ width: 132 }}
        />
      </label>
      {spec ? (
        <label style={{ color: "var(--text-3)" }}>
          {spec.label}{" "}
          <input
            aria-label={spec.label}
            value={record}
            placeholder={spec.example}
            onChange={(e) => setRecord(e.target.value)} style={{ width: 132 }}
          />
        </label>
      ) : null}
      <button
        type="button"
        className="btn"
        data-testid={`run-now-${hand.key}`}
        disabled={run.isPending}
        onClick={() => run.mutate()}
      >
        {run.isPending ? "Running…" : "Run now"}
      </button>
      {run.isSuccess ? (
        <span className="pill green">
          {run.data.verdict === "auto_completed"
            ? "completed unattended"
            : run.data.verdict === "blocked_pre"
              ? "stopped — no model call"
              : run.data.verdict}
        </span>
      ) : null}
      {failure ? (
        <span data-testid="run-now-failed" style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
          <span className="pill red">Could not run — {failedAtWords(failure.failedAt)}</span>
          <Link to={`/workspaces/${workspaceId}/workforce/runs/${failure.runId}`}>open the run</Link>
        </span>
      ) : run.isError ? (
        <span className="pill red">
          {apiErrorMessage(run.error)}
        </span>
      ) : null}
    </div>
  );
}

function HandCard({
  hand,
  workspaceId,
}: {
  hand: HandSummary;
  workspaceId: string;
}) {
  const navigate = useNavigate();
  const persona = usePersona(workspaceId);
  return (
    <div
      className="play-card"
      data-testid={`hand-${hand.key}`}
      onClick={() =>
        navigate(`/workspaces/${workspaceId}/workforce?hand=${hand.key}`)
      }
      style={{ cursor: "pointer" }}
    >
      <div className="play-head">
        <div>
          <div className="play-arch">{handRoleLabel(hand.role_key) ?? "Agent"}</div>
          <div className="play-title">{hand.name}</div>
        </div>
        <div className="play-status">
          <span className={`pill ${hand.runs > 0 ? "green" : "blue"}`}>
            {hand.runs > 0 ? "active" : "idle"}
          </span>
        </div>
      </div>
      <div className="play-meta">
        <span>
          Runs <span className="v">{hand.runs}</span>
        </span>
        <span>
          Unattended <span className="v">{hand.auto_completed}</span>
        </span>
        <span>
          To a person <span className="v">{hand.escalated + hand.blocked_pre}</span>
        </span>
      </div>
      <VerdictBar
        autoCompleted={hand.auto_completed}
        toPerson={hand.escalated + hand.blocked_pre}
        failed={hand.failed}
      />
      <div className="play-foot">
        <span>{scheduleLabel(hand.trigger, hand.record?.label ?? null)}</span>
        <span>last run {relative(hand.last_run_at)}</span>
        {hand.next_run_at ? <span>next {formatWhen(hand.next_run_at)}</span> : null}
        {!hand.scheduled ? <span className="v">not scheduled yet</span> : null}
      </div>
      {hand.last_error ? (
        // The schedule's own failure, kept where someone can see it rather
        // than only in the server log.
        <div className="play-meta" data-testid={`sched-error-${hand.key}`}>
          <span className="pill red">Last scheduled run failed: {hand.last_error}</span>
        </div>
      ) : null}
      {/* What law this agent applies. The roster could say when it runs and
          not what it checks against, which is the first thing a compliance
          reviewer asks about an agent they did not build. */}
      {(hand.packs ?? []).length > 0 ? (
        <div className="play-meta" data-testid={`packs-${hand.key}`}>
          <span>
            Checks against{" "}
            <span className="v">{hand.packs!.map((p) => p.label).join(", ")}</span>
          </span>
        </div>
      ) : null}
      {hand.settings?.owner_name ? (
        <div className="play-meta">
          <span>
            Answers to <span className="v">{hand.settings.owner_name}</span>
            {hand.settings.owner_title ? ` · ${hand.settings.owner_title}` : ""}
          </span>
        </div>
      ) : null}
      <RunNow hand={hand} workspaceId={workspaceId} />
      <AgentSettings
        workspaceId={workspaceId}
        handKey={hand.key}
        handName={hand.name}
        schedule={hand.settings ?? null}
        decisions={hand.decisions ?? []}
        canConfigure={persona === "cxo" || persona === "agent_handler"}
        canSetApprover={persona === "cxo"}
      />
    </div>
  );
}

function RunRow({
  run,
  workspaceId,
}: {
  run: RunSummary;
  workspaceId: string;
}) {
  const navigate = useNavigate();
  return (
    <div
      className="play-card"
      data-testid={`run-${run.run_id}`}
      onClick={() =>
        navigate(`/workspaces/${workspaceId}/workforce/runs/${run.run_id}`)
      }
      style={{ cursor: "pointer" }}
    >
      <div className="play-head">
        <div>
          <div className="play-arch">{run.hand_name}</div>
          <div className="play-title">{verdictLabel(run.verdict)}</div>
        </div>
        <div className="play-status">
          <span className={`pill ${verdictTone(run.verdict)}`}>
            {run.verdict === "blocked_pre"
              ? "no model call"
              : run.verdict === "auto_completed"
                ? "unattended"
                : run.verdict === "escalated"
                  ? "with a person"
                  : "failed"}
          </span>
        </div>
      </div>
      <div className="play-meta">
        {run.anomaly_count > 0 ? (
          <span>
            Anomalies <span className="v">{run.anomaly_count}</span>
          </span>
        ) : null}
        {run.missing.length > 0 ? (
          <span>
            Missing <span className="v">{run.missing.join(", ")}</span>
          </span>
        ) : null}
        <span>
          Took <span className="v">{(run.duration_ms / 1000).toFixed(1)}s</span>
        </span>
      </div>
      <div className="play-foot">
        <span>{formatWhen(run.created_at)}</span>
        {/* No model means none was called, which is worth saying out loud. */}
        <span>{run.llm_model ?? "no model called"}</span>
      </div>
    </div>
  );
}

function WorkforceInner() {
  const { workspaceId = "" } = useParams();

  const roster = useQuery<{ hands: HandSummary[] }, Error>({
    queryKey: ["workforce", workspaceId],
    enabled: !!workspaceId,
    queryFn: () =>
      apiGet<{ hands: HandSummary[] }>(
        `/api/workspaces/${workspaceId}/workforce`,
      ),
  });

  const runs = useQuery<{ runs: RunSummary[] }, Error>({
    queryKey: ["hand-runs", workspaceId],
    enabled: !!workspaceId,
    queryFn: () =>
      apiGet<{ runs: RunSummary[] }>(
        `/api/workspaces/${workspaceId}/hand-runs?limit=25`,
      ),
  });

  const hands = roster.data?.hands ?? [];
  const recent = runs.data?.runs ?? [];
  const unattended = recent.filter((r) => r.verdict === "auto_completed").length;
  const toPerson = recent.filter(
    (r) => r.verdict === "escalated" || r.verdict === "blocked_pre",
  ).length;

  return (
    <div className="main-inner" data-testid="workforce">
      <div className="greet-row">
        <span className="greet">Workforce</span>
      </div>
      <div className="subgreet">
        <div className="section-eyebrow">AGENTS</div>
        <div className="lede">
          {hands.length} {hands.length === 1 ? "agent" : "agents"} on the roster
          {recent.length > 0
            ? ` · ${unattended} of the last ${recent.length} runs completed unattended, ${toPerson} went to a person`
            : ""}
        </div>
      </div>

      <div className="section-h">
        <span className="ttl">Roster</span>
      </div>
      <div className="play-grid">
        {roster.isLoading ? (
          <div className="play-card" data-testid="workforce-loading">
            <div className="play-title">Loading…</div>
          </div>
        ) : hands.length === 0 ? (
          <div className="play-card" data-testid="workforce-empty">
            <div className="play-title">No agents registered.</div>
          </div>
        ) : (
          hands.map((h) => (
            <HandCard key={h.key} hand={h} workspaceId={workspaceId} />
          ))
        )}
      </div>

      <div className="section-h">
        <span className="ttl">Recent runs</span>
      </div>
      <div className="play-grid">
        {runs.isLoading ? (
          <div className="play-card">
            <div className="play-title">Loading…</div>
          </div>
        ) : recent.length === 0 ? (
          <div className="play-card" data-testid="runs-empty">
            <div className="play-title">No runs yet.</div>
          </div>
        ) : (
          recent.map((r) => (
            <RunRow key={r.run_id} run={r} workspaceId={workspaceId} />
          ))
        )}
      </div>
    </div>
  );
}

export function Workforce() {
  return (
    <ErrorBoundary
      fallbackRender={({ error }) => (
        <div className="main-inner" data-testid="workforce-error">
          <div className="greet-row">
            <span className="greet">Workforce</span>
          </div>
          <div className="lede">Could not load the roster: {error.message}</div>
        </div>
      )}
    >
      <WorkforceInner />
    </ErrorBoundary>
  );
}
